// src/components/tactic/TacticCanvas.jsx
// ─────────────────────────────────────────────────────────────────────────
// The board itself: map render, hero tokens, movement paths, freehand marks and
// the calibration modes, all drawn into one SVG laid over map.png.
//
// Everything is authored in LOGICAL map coords and projected at paint time, so
// the perspective of the 2.5D render is handled in exactly one place and the
// rest of the board can think in a flat square.
// ─────────────────────────────────────────────────────────────────────────

import React, { useCallback, useMemo, useRef, useState } from "react";
import { getHeroImageUrlWithFallback } from "../../utils/heroCatalog";
import { clampLogical, CORNER_KEYS, CORNER_LABELS } from "../../utils/tactic/homography";
import { BASES, TOWERS, buildObjectives } from "../../utils/tactic/mapTerrain";
import {
  isDrawingVisible,
  sampleTokenHeading,
  sampleTokenPosition,
} from "../../utils/tactic/playbook";

// Native pixel size of public/map.png — the SVG viewBox matches it so screen
// coords and image coords never need a second scale factor.
export const IMG_W = 1920;
export const IMG_H = 1082;

const TOKEN_R = 30; // token radius in local (pre depth-scale) units

export const TEAM_COLORS = {
  blue: "#3b9dff",
  red: "#ff4d6d",
  neutral: "#f5b942",
};

const clamp01 = (v) => Math.max(0, Math.min(1, v));

const EMPTY_SELECTION = new Set();

// Invisible fat stroke under each mark so a 3px line is still clickable.
const HIT_STROKE = 26;

// Below this the rubber band was a click, not a sweep.
const MARQUEE_SLOP = 6;

// Matches clampLogical's default, so a group drag stops exactly where a single
// token drag would.
const FIELD_PAD = 0.004;

/** Pointer position in SVG user units. */
function pointerToSvg(svg, evt) {
  const ctm = svg.getScreenCTM();
  if (!ctm) return { x: 0, y: 0 };
  const inv = ctm.inverse();
  const p =
    typeof DOMPoint === "function"
      ? new DOMPoint(evt.clientX, evt.clientY).matrixTransform(inv)
      : (() => {
          const sp = svg.createSVGPoint();
          sp.x = evt.clientX;
          sp.y = evt.clientY;
          return sp.matrixTransform(inv);
        })();
  return { x: p.x, y: p.y };
}

export default function TacticCanvas({
  playbook,
  projector,
  time,
  tool = "select",
  drawColor = "#ff1026",
  drawWidth = 4,
  selectedIds = EMPTY_SELECTION,
  selectedDrawingId = null,
  showLandmarks = true,
  showPaths = true,
  landmarks,
  calibrating = false,
  landmarkMode = false,
  onTokensMove,
  onSelect,
  onSelectDrawing,
  onEditDrawingText,
  onToggleTower,
  onLandmarkChange,
  onCommitDrawing,
  onCornersChange,
  svgRef: externalSvgRef,
}) {
  const localSvgRef = useRef(null);
  // The page holds a ref too, so it can serialise the board for image export.
  const svgRef = externalSvgRef || localSvgRef;
  const dragRef = useRef(null);
  const [draft, setDraft] = useState(null); // in-progress drawing
  const [marquee, setMarquee] = useState(null); // in-progress rubber-band box

  const toScreen = useCallback(
    (x, y) => {
      const [u, v] = projector.toScreen(x, y);
      return [u * IMG_W, v * IMG_H];
    },
    [projector]
  );

  const polyPoints = useCallback(
    (poly = []) => poly.map(([x, y]) => toScreen(x, y).join(",")).join(" "),
    [toScreen]
  );

  const toLogicalFromEvent = useCallback(
    (evt) => {
      const svg = svgRef.current;
      if (!svg) return [0.5, 0.5];
      const { x, y } = pointerToSvg(svg, evt);
      const [lx, ly] = projector.toLogical(x / IMG_W, y / IMG_H);
      return clampLogical(lx, ly);
    },
    [projector, svgRef]
  );

  // ── Where everything stands at `time` ──────────────────────────────────
  const placed = useMemo(
    () =>
      (playbook?.tokens || []).map((tok) => ({
        tok,
        pos: sampleTokenPosition(tok, time),
        heading: sampleTokenHeading(tok, time),
      })),
    [playbook, time]
  );

  const placedById = useMemo(() => {
    const map = new Map();
    for (const p of placed) map.set(p.tok.id, p);
    return map;
  }, [placed]);

  // ── Pointer handling ───────────────────────────────────────────────────

  const startTokenDrag = (evt, tokenId) => {
    if (calibrating) return;
    evt.stopPropagation();

    // A modifier click edits the selection and never starts a drag — otherwise
    // shift-clicking the fifth hero would also nudge the other four.
    if (evt.shiftKey || evt.metaKey || evt.ctrlKey) {
      onSelect?.([tokenId], "toggle");
      return;
    }

    // Grabbing a member of the current selection drags the whole group;
    // grabbing anything else narrows the selection to it first.
    const inGroup = selectedIds.has(tokenId);
    if (!inGroup) onSelect?.([tokenId], "replace");
    if (tool !== "select") return;

    const group = inGroup ? [...selectedIds] : [tokenId];
    const origins = group
      .map((id) => placedById.get(id))
      .filter(Boolean)
      .map(({ tok, pos }) => ({ id: tok.id, x: pos.x, y: pos.y }));
    if (!origins.length) return;

    evt.currentTarget.setPointerCapture?.(evt.pointerId);
    const [ox, oy] = toLogicalFromEvent(evt);
    dragRef.current = {
      type: "token",
      origin: [ox, oy],
      origins,
      bounds: {
        minX: Math.min(...origins.map((o) => o.x)),
        maxX: Math.max(...origins.map((o) => o.x)),
        minY: Math.min(...origins.map((o) => o.y)),
        maxY: Math.max(...origins.map((o) => o.y)),
      },
    };
  };

  const startCornerDrag = (evt, key) => {
    evt.stopPropagation();
    evt.currentTarget.setPointerCapture?.(evt.pointerId);
    dragRef.current = { type: "corner", key };
  };

  /** Dragging either half of a mirrored pair moves the stored anchor. */
  const startLandmarkDrag = (evt, objective) => {
    evt.stopPropagation();
    evt.currentTarget.setPointerCapture?.(evt.pointerId);
    dragRef.current = {
      type: "landmark",
      anchorId: objective.anchorId,
      mirrored: objective.mirrored,
    };
  };

  const handleBackgroundDown = (evt) => {
    if (calibrating) return;

    if (tool === "select") {
      // Rubber band. The selection is only applied on pointer-up, so a plain
      // click on empty map still reads as "deselect everything".
      const svg = svgRef.current;
      if (!svg) return;
      const p = pointerToSvg(svg, evt);
      evt.currentTarget.setPointerCapture?.(evt.pointerId);
      dragRef.current = {
        type: "marquee",
        additive: evt.shiftKey || evt.metaKey || evt.ctrlKey,
      };
      setMarquee({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
      return;
    }

    const [x, y] = toLogicalFromEvent(evt);

    if (tool === "text") {
      // eslint-disable-next-line no-alert
      const text = window.prompt("ข้อความ:");
      if (text) onCommitDrawing?.({ tool: "text", color: drawColor, width: drawWidth, points: [[x, y]], text });
      return;
    }
    if (tool === "marker") {
      onCommitDrawing?.({ tool: "marker", color: drawColor, width: drawWidth, points: [[x, y]], text: "" });
      return;
    }

    evt.currentTarget.setPointerCapture?.(evt.pointerId);
    dragRef.current = { type: "draw" };
    setDraft({ tool, color: drawColor, width: drawWidth, points: [[x, y], [x, y]] });
  };

  const handleMove = (evt) => {
    const drag = dragRef.current;
    if (!drag) return;

    if (drag.type === "corner") {
      const svg = svgRef.current;
      if (!svg) return;
      const { x, y } = pointerToSvg(svg, evt);
      onCornersChange?.(drag.key, [clamp01(x / IMG_W), clamp01(y / IMG_H)]);
      return;
    }

    if (drag.type === "marquee") {
      const svg = svgRef.current;
      if (!svg) return;
      const p = pointerToSvg(svg, evt);
      setMarquee((m) => (m ? { ...m, x2: p.x, y2: p.y } : m));
      return;
    }

    const [x, y] = toLogicalFromEvent(evt);

    if (drag.type === "landmark") {
      // Grabbing the twin means the anchor sits at the opposite point.
      const point = drag.mirrored ? [1 - x, 1 - y] : [x, y];
      onLandmarkChange?.(drag.anchorId, point);
      return;
    }

    if (drag.type === "token") {
      // The group moves as one rigid body: clamp the DELTA against the group's
      // bounding box rather than each token separately, so a rotation dragged
      // into the map edge keeps its shape instead of collapsing onto the wall.
      const { minX, maxX, minY, maxY } = drag.bounds;
      const dx = Math.max(FIELD_PAD - minX, Math.min(1 - FIELD_PAD - maxX, x - drag.origin[0]));
      const dy = Math.max(FIELD_PAD - minY, Math.min(1 - FIELD_PAD - maxY, y - drag.origin[1]));
      onTokensMove?.(drag.origins.map((o) => ({ id: o.id, x: o.x + dx, y: o.y + dy })));
      return;
    }

    if (drag.type === "draw") {
      setDraft((d) => {
        if (!d) return d;
        if (d.tool === "pen") {
          const last = d.points[d.points.length - 1];
          // Thin the trail so a long stroke stays a few hundred points, not
          // a few thousand — it has to survive JSON export.
          if (Math.hypot(x - last[0], y - last[1]) < 0.004) return d;
          return { ...d, points: [...d.points, [x, y]] };
        }
        return { ...d, points: [d.points[0], [x, y]] };
      });
    }
  };

  const handleUp = () => {
    const drag = dragRef.current;
    dragRef.current = null;

    if (drag?.type === "marquee") {
      if (marquee) {
        const w = Math.abs(marquee.x2 - marquee.x1);
        const h = Math.abs(marquee.y2 - marquee.y1);
        if (w < MARQUEE_SLOP && h < MARQUEE_SLOP) {
          if (!drag.additive) onSelect?.([], "replace");
        } else {
          const x0 = Math.min(marquee.x1, marquee.x2);
          const x1 = Math.max(marquee.x1, marquee.x2);
          const y0 = Math.min(marquee.y1, marquee.y2);
          const y1 = Math.max(marquee.y1, marquee.y2);
          const caught = placed
            .filter(({ pos }) => {
              const [sx, sy] = toScreen(pos.x, pos.y);
              return sx >= x0 && sx <= x1 && sy >= y0 && sy <= y1;
            })
            .map(({ tok }) => tok.id);
          onSelect?.(caught, drag.additive ? "add" : "replace");
        }
      }
      setMarquee(null);
      return;
    }

    if (drag?.type === "draw" && draft) {
      const meaningful =
        draft.tool === "pen"
          ? draft.points.length > 2
          : Math.hypot(
              draft.points[1][0] - draft.points[0][0],
              draft.points[1][1] - draft.points[0][1]
            ) > 0.01;
      if (meaningful) onCommitDrawing?.(draft);
      setDraft(null);
    }
  };

  // ── Drawing primitives ─────────────────────────────────────────────────

  const renderDrawing = (d, key, ghost = false) => {
    const opacity = ghost ? 0.7 : 1;
    const pts = d.points || [];
    if (!pts.length) return null;
    const stroke = d.color;
    const w = d.width;

    if (d.tool === "pen" || d.tool === "line") {
      const path = (d.tool === "pen" ? pts : [pts[0], pts[pts.length - 1]])
        .map(([x, y], i) => `${i ? "L" : "M"}${toScreen(x, y).join(" ")}`)
        .join(" ");
      return (
        <path
          key={key}
          d={path}
          fill="none"
          stroke={stroke}
          strokeWidth={w}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={opacity}
        />
      );
    }

    if (d.tool === "arrow") {
      const [a, b] = [pts[0], pts[pts.length - 1]];
      const [ax, ay] = toScreen(a[0], a[1]);
      const [bx, by] = toScreen(b[0], b[1]);
      const ang = Math.atan2(by - ay, bx - ax);
      const head = Math.max(16, w * 4);
      const wing = head * 0.55;
      const tip = [bx, by];
      const left = [bx - head * Math.cos(ang - 0.4), by - head * Math.sin(ang - 0.4)];
      const right = [bx - head * Math.cos(ang + 0.4), by - head * Math.sin(ang + 0.4)];
      return (
        <g key={key} opacity={opacity}>
          <line
            x1={ax}
            y1={ay}
            x2={bx - Math.cos(ang) * head * 0.7}
            y2={by - Math.sin(ang) * head * 0.7}
            stroke={stroke}
            strokeWidth={w}
            strokeLinecap="round"
          />
          <polygon
            points={`${tip[0]},${tip[1]} ${left[0]},${left[1]} ${right[0]},${right[1]}`}
            fill={stroke}
          />
          <circle cx={ax} cy={ay} r={wing * 0.22} fill={stroke} />
        </g>
      );
    }

    if (d.tool === "rect") {
      const [a, b] = [pts[0], pts[pts.length - 1]];
      const quad = [
        [a[0], a[1]],
        [b[0], a[1]],
        [b[0], b[1]],
        [a[0], b[1]],
      ];
      return (
        <polygon
          key={key}
          points={polyPoints(quad)}
          fill={`${stroke}22`}
          stroke={stroke}
          strokeWidth={w}
          opacity={opacity}
        />
      );
    }

    if (d.tool === "circle") {
      // Tessellated in logical space so perspective bends it correctly.
      const [a, b] = [pts[0], pts[pts.length - 1]];
      const r = Math.hypot(b[0] - a[0], b[1] - a[1]);
      const ring = Array.from({ length: 48 }, (_, i) => {
        const t = (i / 48) * Math.PI * 2;
        return [a[0] + Math.cos(t) * r, a[1] + Math.sin(t) * r];
      });
      return (
        <polygon
          key={key}
          points={polyPoints(ring)}
          fill={`${stroke}1f`}
          stroke={stroke}
          strokeWidth={w}
          opacity={opacity}
        />
      );
    }

    if (d.tool === "text") {
      const [x, y] = toScreen(pts[0][0], pts[0][1]);
      return (
        <text
          key={key}
          x={x}
          y={y}
          fill={stroke}
          fontSize={30}
          fontWeight={700}
          textAnchor="middle"
          style={{ paintOrder: "stroke", stroke: "#000", strokeWidth: 6, fontFamily: "Kanit, sans-serif" }}
          opacity={opacity}
        >
          {d.text}
        </text>
      );
    }

    if (d.tool === "marker") {
      const [x, y] = toScreen(pts[0][0], pts[0][1]);
      return (
        <g key={key} opacity={opacity}>
          <circle cx={x} cy={y} r={16} fill="none" stroke={stroke} strokeWidth={4}>
            <animate attributeName="r" values="10;22;10" dur="1.6s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="1;0.2;1" dur="1.6s" repeatCount="indefinite" />
          </circle>
          <circle cx={x} cy={y} r={7} fill={stroke} />
        </g>
      );
    }
    return null;
  };

  /** Path through a mark's points, in screen units. */
  const drawingPath = (d) => {
    const pts = d.points || [];
    const use = d.tool === "pen" ? pts : [pts[0], pts[pts.length - 1]];
    return use.map(([x, y], i) => `${i ? "L" : "M"}${toScreen(x, y).join(" ")}`).join(" ");
  };

  /** The quad or ring a rect/circle mark occupies, in logical points. */
  const drawingShape = (d) => {
    const pts = d.points || [];
    const [a, b] = [pts[0], pts[pts.length - 1]];
    if (d.tool === "rect") {
      return [
        [a[0], a[1]],
        [b[0], a[1]],
        [b[0], b[1]],
        [a[0], b[1]],
      ];
    }
    const r = Math.hypot(b[0] - a[0], b[1] - a[1]);
    return Array.from({ length: 48 }, (_, i) => {
      const t = (i / 48) * Math.PI * 2;
      return [a[0] + Math.cos(t) * r, a[1] + Math.sin(t) * r];
    });
  };

  /**
   * A transparent, deliberately fat copy of each mark. Without it a 3px arrow
   * would need pixel-perfect aim to select.
   */
  const renderHitArea = (d) => {
    const pts = d.points || [];
    if (!pts.length) return null;

    if (d.tool === "text" || d.tool === "marker") {
      const [x, y] = toScreen(pts[0][0], pts[0][1]);
      return <circle cx={x} cy={y} r={26} fill="transparent" />;
    }
    if (d.tool === "rect" || d.tool === "circle") {
      // A transparent fill makes the interior grabbable too, not just the edge.
      return (
        <polygon
          points={polyPoints(drawingShape(d))}
          fill="transparent"
          stroke="transparent"
          strokeWidth={HIT_STROKE}
        />
      );
    }
    return (
      <path
        d={drawingPath(d)}
        fill="none"
        stroke="transparent"
        strokeWidth={HIT_STROKE}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    );
  };

  /** White outline behind the selected mark. */
  const renderHalo = (d) => {
    const pts = d.points || [];
    if (!pts.length) return null;
    const halo = { fill: "none", stroke: "#fff", opacity: 0.5 };

    if (d.tool === "text" || d.tool === "marker") {
      const [x, y] = toScreen(pts[0][0], pts[0][1]);
      return <circle cx={x} cy={y} r={26} {...halo} strokeWidth={3} strokeDasharray="7 5" />;
    }
    if (d.tool === "rect" || d.tool === "circle") {
      return (
        <polygon
          points={polyPoints(drawingShape(d))}
          {...halo}
          strokeWidth={(d.width || 3) + 8}
        />
      );
    }
    return (
      <path
        d={drawingPath(d)}
        {...halo}
        strokeWidth={(d.width || 3) + 8}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    );
  };

  const visibleDrawings = (playbook?.drawings || []).filter((d) => isDrawingVisible(d, time));
  const marksInteractive = !calibrating && tool === "select";
  const towersInteractive = marksInteractive && showLandmarks;
  const downedTowers = useMemo(
    () => new Set(playbook?.downedTowers || []),
    [playbook]
  );
  const objectives = useMemo(() => buildObjectives(landmarks), [landmarks]);

  const interactive = !calibrating;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: `${IMG_W} / ${IMG_H}`,
        borderRadius: 14,
        overflow: "hidden",
        background: "#05070c",
        border: "1px solid rgba(255,255,255,0.08)",
        userSelect: "none",
        touchAction: "none",
      }}
    >
      <img
        // 305KB webp, with the 720KB jpg behind it for anything that cannot
        // decode webp. The 8.3MB map.png source lives in assets/ and is
        // deliberately outside public/ so it never reaches a build.
        src={`${process.env.PUBLIC_URL}/map.webp`}
        onError={(e) => {
          if (!e.currentTarget.dataset.fellBack) {
            e.currentTarget.dataset.fellBack = "1";
            e.currentTarget.src = `${process.env.PUBLIC_URL}/map.jpg`;
          }
        }}
        alt="แผนที่ Antaris Battlefield"
        draggable={false}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "fill",
          pointerEvents: "none",
        }}
      />

      <svg
        ref={svgRef}
        viewBox={`0 0 ${IMG_W} ${IMG_H}`}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          cursor: tool === "select" ? "default" : "crosshair",
        }}
        onPointerDown={handleBackgroundDown}
        onPointerMove={handleMove}
        onPointerUp={handleUp}
        // No onPointerLeave: every drag holds a pointer capture, so leaving the
        // SVG must not drop the gesture — dragging a hero to the map edge used
        // to cut the drag the moment the cursor crossed the border.
      >
        <defs>
          <clipPath id="tacticTokenClip">
            <circle cx="0" cy="0" r={TOKEN_R} />
          </clipPath>
        </defs>

        {/* Landmarks */}
        {showLandmarks && (
          <g>
            {TOWERS.map((t) => {
              const [x, y] = toScreen(t.pos[0], t.pos[1]);
              const s = projector.scaleAt(t.pos[0], t.pos[1]);
              const down = downedTowers.has(t.id);
              const r = 8 * s;
              return (
                <g
                  key={t.id}
                  style={{ cursor: towersInteractive ? "pointer" : "default" }}
                  onPointerDown={
                    towersInteractive
                      ? (e) => {
                          e.stopPropagation();
                          onToggleTower?.(t.id);
                        }
                      : undefined
                  }
                >
                  <rect
                    x={x - r}
                    y={y - r}
                    width={r * 2}
                    height={r * 2}
                    // Near-opaque with a dark edge: at 0.55 these washed out
                    // against the base platforms, which glow in the same colour.
                    fill={down ? "rgba(6,9,15,0.6)" : TEAM_COLORS[t.side]}
                    opacity={0.92}
                    stroke={down ? TEAM_COLORS[t.side] : "rgba(0,0,0,0.75)"}
                    strokeWidth={2.5}
                    strokeDasharray={down ? "4 3" : undefined}
                    transform={`rotate(45 ${x} ${y})`}
                  />
                  {down && (
                    <path
                      d={`M ${x - r * 0.7} ${y - r * 0.7} L ${x + r * 0.7} ${y + r * 0.7}
                          M ${x + r * 0.7} ${y - r * 0.7} L ${x - r * 0.7} ${y + r * 0.7}`}
                      stroke={TEAM_COLORS[t.side]}
                      strokeWidth={2.5}
                      opacity={0.8}
                      strokeLinecap="round"
                    />
                  )}
                  {/* Comfortable click target regardless of depth scale. */}
                  {towersInteractive && <circle cx={x} cy={y} r={18} fill="transparent" />}
                </g>
              );
            })}
          </g>
        )}

        {showLandmarks && (
          <g pointerEvents="none">
            {/* Cores. A plain tinted disc vanished here — the base platform is
                already lit in the team's own colour, so the marker was blue on
                blue. It needs its own dark ground to sit on. */}
            {BASES.map((b) => {
              const [x, y] = toScreen(b.pos[0], b.pos[1]);
              const s = projector.scaleAt(b.pos[0], b.pos[1]);
              const r = 15 * s;
              const color = TEAM_COLORS[b.side];
              return (
                <g key={b.side}>
                  <circle cx={x} cy={y} r={r + 5} fill="rgba(6,9,15,0.72)" />
                  <circle cx={x} cy={y} r={r} fill="none" stroke={color} strokeWidth={4} />
                  <circle cx={x} cy={y} r={r * 0.38} fill={color} />
                  <text
                    x={x}
                    y={y - r - 12}
                    fill={color}
                    fontSize={17}
                    fontWeight={700}
                    textAnchor="middle"
                    style={{
                      paintOrder: "stroke",
                      stroke: "#000",
                      strokeWidth: 5,
                      fontFamily: "Kanit, sans-serif",
                    }}
                  >
                    ป้อมใหญ่
                  </text>
                </g>
              );
            })}
            {objectives.map((o) => {
              const [x, y] = toScreen(o.pos[0], o.pos[1]);
              return (
                <g key={o.id}>
                  <circle cx={x} cy={y} r={13} fill={o.color} opacity={0.75} />
                  <text
                    x={x}
                    y={y - 20}
                    fill={o.color}
                    fontSize={19}
                    fontWeight={700}
                    textAnchor="middle"
                    style={{ paintOrder: "stroke", stroke: "#000", strokeWidth: 5, fontFamily: "Kanit, sans-serif" }}
                  >
                    {o.short}
                  </text>
                </g>
              );
            })}
          </g>
        )}

        {/* Movement paths.
            A token eases between keyframes, but easing only bends TIME — the
            route between two keys is a straight line in logical space, and a
            homography keeps straight lines straight. So the path is exactly the
            polyline through the keys, with no sampling needed. */}
        {showPaths && (
          <g pointerEvents="none">
            {placed.map(({ tok }) => {
              const keys = tok.keys || [];
              if (keys.length < 2) return null;

              const selected = selectedIds.has(tok.id);
              const color = TEAM_COLORS[tok.team] || TEAM_COLORS.neutral;
              const pts = keys.map((k) => toScreen(k.x, k.y));
              const d = pts.map(([x, y], i) => `${i ? "L" : "M"}${x} ${y}`).join(" ");

              // Arrowhead on the final leg, so the direction of travel reads
              // without having to press play.
              const [ax, ay] = pts[pts.length - 2];
              const [bx, by] = pts[pts.length - 1];
              const ang = Math.atan2(by - ay, bx - ax);
              const head = selected ? 17 : 12;

              return (
                <g key={`path-${tok.id}`} opacity={selected ? 0.95 : 0.3}>
                  <path
                    d={d}
                    fill="none"
                    stroke={color}
                    strokeWidth={selected ? 4 : 2.5}
                    strokeDasharray={selected ? undefined : "10 8"}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <polygon
                    points={[
                      `${bx},${by}`,
                      `${bx - head * Math.cos(ang - 0.42)},${by - head * Math.sin(ang - 0.42)}`,
                      `${bx - head * Math.cos(ang + 0.42)},${by - head * Math.sin(ang + 0.42)}`,
                    ].join(" ")}
                    fill={color}
                  />
                  {selected &&
                    pts.map(([x, y], i) => (
                      <rect
                        key={`k-${keys[i].t}`}
                        x={x - 5}
                        y={y - 5}
                        width={10}
                        height={10}
                        transform={`rotate(45 ${x} ${y})`}
                        fill="#0a0d14"
                        stroke={color}
                        strokeWidth={2.5}
                      />
                    ))}
                </g>
              );
            })}
          </g>
        )}

        {/* Committed marks, then the one being drawn.
            Marks only accept clicks under the select tool, so drawing a new
            arrow across an old one never selects the old one instead. */}
        <g pointerEvents={marksInteractive ? "auto" : "none"}>
          {visibleDrawings.map((d) => (
            <g
              key={d.id}
              style={{ cursor: marksInteractive ? "pointer" : "inherit" }}
              onPointerDown={
                marksInteractive
                  ? (e) => {
                      e.stopPropagation();
                      onSelectDrawing?.(d.id);
                    }
                  : undefined
              }
              onDoubleClick={
                marksInteractive && d.tool === "text"
                  ? (e) => {
                      e.stopPropagation();
                      onEditDrawingText?.(d.id);
                    }
                  : undefined
              }
            >
              {marksInteractive && renderHitArea(d)}
              {d.id === selectedDrawingId && renderHalo(d)}
              {renderDrawing(d, d.id)}
            </g>
          ))}
        </g>
        <g pointerEvents="none">{draft && renderDrawing(draft, "draft", true)}</g>

        {/* Tokens — sorted so the far side of the map paints first */}
        <g>
          {[...placed]
            .sort((a, b) => a.pos.x + a.pos.y - (b.pos.x + b.pos.y))
            .reverse()
            .map(({ tok, pos, heading }) => {
              const [sx, sy] = toScreen(pos.x, pos.y);
              const scale = projector.scaleAt(pos.x, pos.y);
              const color = TEAM_COLORS[tok.team] || TEAM_COLORS.neutral;
              const selected = selectedIds.has(tok.id);

              const img = tok.kind === "hero" ? getHeroImageUrlWithFallback(tok.heroName) : null;

              return (
                <g
                  key={tok.id}
                  transform={`translate(${sx} ${sy}) scale(${scale})`}
                  style={{ cursor: interactive && tool === "select" ? "grab" : "inherit" }}
                  onPointerDown={(e) => startTokenDrag(e, tok.id)}
                >
                  <ellipse cx={0} cy={TOKEN_R * 0.85} rx={TOKEN_R * 0.8} ry={TOKEN_R * 0.28} fill="rgba(0,0,0,0.45)" />

                  {heading != null && (
                    <path
                      d={`M ${TOKEN_R + 6} 0 L ${TOKEN_R + 22} -9 L ${TOKEN_R + 22} 9 Z`}
                      fill={color}
                      opacity={0.9}
                      transform={`rotate(${(heading * 180) / Math.PI})`}
                    />
                  )}

                  <circle cx={0} cy={0} r={TOKEN_R + 3} fill="#0a0d14" />
                  {img ? (
                    <image
                      href={img}
                      x={-TOKEN_R}
                      y={-TOKEN_R}
                      width={TOKEN_R * 2}
                      height={TOKEN_R * 2}
                      clipPath="url(#tacticTokenClip)"
                      preserveAspectRatio="xMidYMid slice"
                    />
                  ) : (
                    <circle cx={0} cy={0} r={TOKEN_R} fill={`${color}33`} />
                  )}

                  <circle
                    cx={0}
                    cy={0}
                    r={TOKEN_R + 2}
                    fill="none"
                    stroke={color}
                    strokeWidth={selected ? 6 : 4}
                  />
                  {selected && (
                    <circle cx={0} cy={0} r={TOKEN_R + 11} fill="none" stroke="#fff" strokeWidth={2.5} opacity={0.85} />
                  )}

                  {(tok.role || tok.label) && (
                    <text
                      x={0}
                      y={TOKEN_R + 24}
                      fill="#fff"
                      fontSize={20}
                      fontWeight={700}
                      textAnchor="middle"
                      style={{ paintOrder: "stroke", stroke: "#000", strokeWidth: 5, fontFamily: "Kanit, sans-serif" }}
                    >
                      {tok.label || tok.role}
                    </text>
                  )}
                </g>
              );
            })}
        </g>

        {/* Landmark editing — drag a marker onto the real camp. Each pair is
            linked through its anchor, so placing one places both and the map
            cannot end up lopsided. */}
        {landmarkMode && (
          <g>
            {objectives.map((o) => {
              const [x, y] = toScreen(o.pos[0], o.pos[1]);
              return (
                <g
                  key={`lm-${o.id}`}
                  style={{ cursor: "grab" }}
                  onPointerDown={(e) => startLandmarkDrag(e, o)}
                >
                  <circle
                    cx={x}
                    cy={y}
                    r={24}
                    fill={`${o.color}33`}
                    stroke={o.color}
                    strokeWidth={3}
                    strokeDasharray="7 5"
                  />
                  <circle cx={x} cy={y} r={4} fill="#fff" />
                  <text
                    x={x}
                    y={y + 40}
                    fill="#fff"
                    fontSize={16}
                    fontWeight={600}
                    textAnchor="middle"
                    style={{
                      paintOrder: "stroke",
                      stroke: "#000",
                      strokeWidth: 5,
                      fontFamily: "Kanit, sans-serif",
                    }}
                  >
                    {o.label}
                  </text>
                </g>
              );
            })}
          </g>
        )}

        {/* Rubber band */}
        {marquee && (
          <rect
            x={Math.min(marquee.x1, marquee.x2)}
            y={Math.min(marquee.y1, marquee.y2)}
            width={Math.abs(marquee.x2 - marquee.x1)}
            height={Math.abs(marquee.y2 - marquee.y1)}
            fill="rgba(255,16,38,0.12)"
            stroke="#ff1026"
            strokeWidth={2}
            strokeDasharray="8 6"
            pointerEvents="none"
          />
        )}

        {/* Calibration handles */}
        {calibrating && (
          <g>
            <polygon
              points={CORNER_KEYS.map((k) => {
                const [u, v] = projector.corners[k];
                return `${u * IMG_W},${v * IMG_H}`;
              }).join(" ")}
              fill="rgba(255,16,38,0.10)"
              stroke="#ff1026"
              strokeWidth={3}
              strokeDasharray="12 8"
            />
            {CORNER_KEYS.map((k) => {
              const [u, v] = projector.corners[k];
              const x = u * IMG_W;
              const y = v * IMG_H;
              return (
                <g key={k} style={{ cursor: "grab" }} onPointerDown={(e) => startCornerDrag(e, k)}>
                  <circle cx={x} cy={y} r={22} fill="rgba(255,16,38,0.35)" stroke="#fff" strokeWidth={3} />
                  <circle cx={x} cy={y} r={5} fill="#fff" />
                  <text
                    x={x}
                    y={y - 32}
                    fill="#fff"
                    fontSize={22}
                    fontWeight={700}
                    textAnchor="middle"
                    style={{ paintOrder: "stroke", stroke: "#000", strokeWidth: 6, fontFamily: "Kanit, sans-serif" }}
                  >
                    {CORNER_LABELS[k]}
                  </text>
                </g>
              );
            })}
          </g>
        )}
      </svg>
    </div>
  );
}
